<script setup lang="ts">
    import { computed, toRef } from 'vue'
    import type { AttributeDeleterDialogViewModel } from '@Alternatives/Attributes/Components/AttributeDeleterDialog'

    // ---------------- props / emits ----------------

    const props = defineProps<{
        viewModel: AttributeDeleterDialogViewModel
    }>()

    const emit = defineEmits<{
        (e: 'delete-attribute', attributeId: number | null): void
        (e: 'show-dependencies', attributeId: number | null): void
    }>()

    // ---------------- shortcuts ----------------

    const vm = toRef(props, 'viewModel')

    // ---------------- derived ----------------

    const opened = computed(() => vm.value.opened)
    const initialized = computed(() => vm.value.rm != null)

    const attributeName = computed(() => vm.value.attributeName)
    const hasDependants = computed(() => vm.value.rm?.HasDependants ?? true)
    const dependenciesCount = computed(() => vm.value.rm?.Count ?? null)

    // ---------------- methods ----------------

    function emitDeleteAttribute()
    {
        emit('delete-attribute', vm.value.attributeId)
    }

    function emitShowDependencies()
    {
        emit('show-dependencies', vm.value.attributeId)
    }

    function close()
    {
        vm.value.close()
    }
</script>


<template>
    <div class="modal-mask" v-if="opened">
        <div class="modal-wrapper">
            <div class="modal-dialog modal-lg modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header" :class="{'bg-danger': !hasDependants }">
                        <h5 class="modal-title">Delete attribute: {{attributeName}}</h5>
                        <button type="button" class="close" @click="close">&times;</button>
                    </div>

                    <div class="modal-body">
                        <h4 v-if="!initialized">
                            <i class="fa fa-spinner fa-spin"></i> Checking dependencies...
                        </h4>

                        <div v-if="initialized && hasDependants">
                            <div class="alert alert-danger text-dark" role="alert">
                                <h5 class="alert-heading">Cannot delete this attribute</h5>
                                <p>
                                    The attribute '<strong>{{attributeName}}</strong>' is used in <strong>{{dependenciesCount}}</strong> places.
                                    To delete this attribute, first remove it from where it is used.
                                </p>
                            </div>
                        </div>
                        <div v-if="initialized && !hasDependants">
                            <h4>Are you sure you want to delete this attribute?</h4>
                            <p>You are about to delete '<strong>{{attributeName}}</strong>'. This can't be undone.</p>
                        </div>

                    </div>

                    <div class="modal-footer">
                        <button v-if="initialized && hasDependants" type="button" class="btn btn-secondary" @click="emitShowDependencies">View dependencies</button>
                        <button v-if="initialized && !hasDependants" type="button" class="btn btn-danger" @click="emitDeleteAttribute">Delete</button>
                        <button type="button" class="btn btn-outline-secondary" @click="close">Cancel</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>